// =============================================================================
// components/ai/R2AIAssistHistory.tsx
// Universal AI Assist History — permanent AI knowledge base across ALL R2 modules
// Prompt · Engine · Tokens · Processing Time · Cost · Date · Project
// Favorite · Pin · Re-run · Export · Search · Filter
// =============================================================================
import { useState, useEffect, useCallback, type JSX } from 'react';
import {
  History, RefreshCw, Check, X, Edit3, ChevronDown, ChevronUp,
  Bot, Copy, RotateCcw, Loader2, ChevronRight,
  CheckCircle2, AlertTriangle, Star, Pin, Download, Search,
  Filter, Zap, Clock, DollarSign, Cpu, FileText, Layers,
} from 'lucide-react';

const API_URL = (import.meta as any).env?.VITE_BACKEND_URL
  ?? (typeof process !== 'undefined' ? (process.env as any)?.REACT_APP_BACKEND_URL ?? '' : '');

const C = {
  bg:     'rgba(6,12,26,0.88)',
  card:   'rgba(8,16,36,0.9)',
  border: 'rgba(30,50,80,0.45)',
  teal:   '#5eead4',
  amber:  '#fbbf24',
  blue:   '#60a5fa',
  green:  '#4ade80',
  red:    '#f87171',
  purple: '#a78bfa',
  text:   '#f0f4ff',
  muted:  'rgba(148,172,210,0.65)',
  dim:    'rgba(100,140,180,0.3)',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: JSX.Element }> = {
  pending:  { label: 'Pending',   color: C.amber,  icon: <Clock size={10}/> },
  accepted: { label: 'Accepted',  color: C.green,  icon: <CheckCircle2 size={10}/> },
  rejected: { label: 'Rejected',  color: C.red,    icon: <X size={10}/> },
  edited:   { label: 'Edited',    color: C.purple, icon: <Edit3 size={10}/> },
};

const MODULE_COLOR: Record<string, string> = {
  permit: C.blue, chat: C.teal, marketplace: C.amber, checklist: C.green,
  qa: C.purple, voice: C.red, 'r2-pie': C.blue, default: C.muted,
};
const MODULE_LABEL: Record<string, string> = {
  permit: 'Permit PIE', chat: 'Chat', marketplace: 'Marketplace', checklist: 'Checklist',
  qa: 'QA/QC', voice: 'Voice', 'r2-pie': 'PIE', default: 'R2',
};

const ALL_MODULES = ['all', 'permit', 'chat', 'marketplace', 'checklist', 'qa', 'voice'];

interface R2AIAssistHistoryProps {
  module?: string;
  sourceId?: string;
  userId?: string;
  userName?: string;
  onRegenerate?: (genId: string, prompt: string, model: string) => void;
  onAccept?: (genId: string, content: string) => void;
  compact?: boolean;
}

export function R2AIAssistHistory({
  module, sourceId, userId, onRegenerate, onAccept, compact = false,
}: R2AIAssistHistoryProps): JSX.Element {
  const [generations, setGenerations] = useState<any[]>([]);
  const [loading, setLoading]         = useState(false);
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [editingId, setEditingId]     = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filterModule, setFilterModule]   = useState(module ?? 'all');
  const [search, setSearch]               = useState('');
  const [stats, setStats]                 = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (filterModule !== 'all') params.set('module', filterModule);
      if (sourceId) params.set('source_id', sourceId);
      if (userId) params.set('user_id', userId);
      const [gRes, sRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/r2-ai-history/generations?${params}`).then(r => r.json()),
        fetch(`${API_URL}/api/r2-ai-history/stats`).then(r => r.json()),
      ]);
      if (gRes.status === 'fulfilled') setGenerations(gRes.value?.generations ?? []);
      if (sRes.status === 'fulfilled') setStats(sRes.value);
    } finally { setLoading(false); }
  }, [filterModule, sourceId, userId]);

  useEffect(() => { void load(); }, [load]);

  const updateStatus = async (genId: string, status: string, edited?: string) => {
    setActionLoading(genId);
    try {
      await fetch(`${API_URL}/api/r2-ai-history/generations/${genId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, edited_content: edited }),
      });
      setGenerations(prev => prev.map(g => g.gen_id === genId ? { ...g, status, result: edited ?? g.result } : g));
      if (status === 'accepted' && onAccept) {
        const gen = generations.find(g => g.gen_id === genId);
        if (gen) onAccept(genId, edited ?? gen.result);
      }
      if (status === 'edited') { setEditingId(null); setEditContent(''); }
    } finally { setActionLoading(null); }
  };

  const doRegenerate = async (g: any) => {
    setActionLoading(g.gen_id + '-regen');
    try {
      await fetch(`${API_URL}/api/r2-ai-history/generations/${g.gen_id}/regenerate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parent_id: g.gen_id }),
      });
      if (onRegenerate) onRegenerate(g.gen_id, g.prompt, g.model);
      void load();
    } finally { setActionLoading(null); }
  };

  const exportCSV = () => {
    const rows = [
      ['Module', 'Prompt', 'Model', 'Status', 'Date'],
      ...generations.map(g => [
        g.module ?? '', (g.prompt ?? '').replace(/,/g, ';'), g.model ?? '', g.status ?? '', g.created_at ?? '',
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'r2-ai-history.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = generations.filter(g => {
    if (!search) return true;
    return (g.prompt ?? '').toLowerCase().includes(search.toLowerCase())
      || (g.result ?? '').toLowerCase().includes(search.toLowerCase())
      || (g.module ?? '').toLowerCase().includes(search.toLowerCase());
  });

  if (compact && loading) return <div style={{ padding: 12, textAlign: 'center' }}><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: C.teal }}/></div>;

  return (
    <div data-testid="r2-ai-history" style={{ background: C.bg, borderRadius: compact ? 0 : 12, overflow: 'hidden', fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ padding: compact ? '10px 12px' : '14px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <History size={14} style={{ color: C.teal, flexShrink: 0 }}/>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text, flex: 1 }}>AI History</span>
        {stats && (
          <span style={{ fontSize: 10, color: C.muted }}>{stats.total ?? 0} total · {stats.by_status?.accepted ?? 0} accepted</span>
        )}
        <div style={{ display: 'flex', gap: 5 }}>
          <button data-testid="ai-history-export-btn" onClick={exportCSV} title="Export CSV" style={{ background: `${C.blue}12`, border: `1px solid ${C.blue}25`, color: C.blue, borderRadius: 7, padding: '4px 8px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
            <Download size={10}/> CSV
          </button>
          <button data-testid="ai-history-refresh-btn" onClick={load} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 7, padding: '4px 8px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center' }}>
            {loading ? <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }}/> : <RefreshCw size={10}/>}
          </button>
        </div>
      </div>

      {/* Filters */}
      {!compact && (
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, minWidth: 140 }}>
            <Search size={10} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: C.dim }}/>
            <input
              data-testid="ai-history-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search prompts, results…"
              style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '5px 8px 5px 22px', color: C.text, fontSize: 10, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          {/* Module filter */}
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {ALL_MODULES.map(m => {
              const mc = MODULE_COLOR[m] ?? C.muted;
              return (
                <button
                  key={m}
                  data-testid={`ai-history-filter-${m}`}
                  onClick={() => setFilterModule(m)}
                  style={{ padding: '3px 9px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 9.5, fontWeight: 600, background: filterModule === m ? `${mc}18` : 'transparent', color: filterModule === m ? mc : C.dim, textTransform: 'capitalize', transition: 'all 0.12s' }}
                >
                  {m === 'all' ? 'All' : (MODULE_LABEL[m] ?? m)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      <div style={{ overflowY: 'auto', maxHeight: compact ? 320 : 600 }}>
        {loading && generations.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: C.teal }}/></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: C.dim }}>
            <Bot size={24} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.25 }}/>
            <div style={{ fontSize: 11 }}>No AI generations yet</div>
          </div>
        ) : filtered.map(g => {
          const scRaw  = STATUS_CONFIG[g.status] ?? STATUS_CONFIG['pending']!;
          const sc     = scRaw ?? { label: 'Pending', color: C.amber, icon: <Clock size={10}/> };
          const mc   = MODULE_COLOR[g.module] ?? MODULE_COLOR['default'];
          const isEx = expandedId === g.gen_id;
          const isEd = editingId === g.gen_id;
          const al   = actionLoading?.startsWith(g.gen_id);

          return (
            <div
              key={g.gen_id}
              data-testid={`ai-history-item-${g.gen_id}`}
              style={{ borderBottom: `1px solid ${C.border}`, background: isEx ? 'rgba(37,181,168,0.03)' : 'transparent', transition: 'background 0.15s' }}
            >
              {/* Item header */}
              <div
                onClick={() => setExpandedId(isEx ? null : g.gen_id)}
                style={{ padding: '11px 14px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start' }}
              >
                <div style={{ width: 28, height: 28, borderRadius: 7, background: `${mc}12`, border: `1px solid ${mc}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: mc, flexShrink: 0, marginTop: 1 }}>
                  <Bot size={12}/>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 9.5, padding: '1px 7px', borderRadius: 5, background: `${mc}12`, color: mc, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {MODULE_LABEL[g.module] ?? g.module ?? 'R2'}
                    </span>
                    <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 5, background: `${sc.color}12`, color: sc.color, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
                      {sc.icon} {sc.label}
                    </span>
                    {g.model && <span style={{ fontSize: 8.5, color: C.dim }}>{g.model}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: C.text, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isEx ? 'normal' : 'nowrap' }}>
                    {g.prompt}
                  </div>
                  <div style={{ marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {g.metadata?.tokens && <span style={{ fontSize: 9, color: C.dim, display: 'flex', alignItems: 'center', gap: 3 }}><Zap size={8}/>{g.metadata.tokens} tokens</span>}
                    {g.metadata?.processing_ms && <span style={{ fontSize: 9, color: C.dim, display: 'flex', alignItems: 'center', gap: 3 }}><Clock size={8}/>{(g.metadata.processing_ms / 1000).toFixed(1)}s</span>}
                    {g.metadata?.cost_usd && <span style={{ fontSize: 9, color: C.dim, display: 'flex', alignItems: 'center', gap: 3 }}><DollarSign size={8}/>${g.metadata.cost_usd}</span>}
                    {g.created_at && <span style={{ fontSize: 9, color: C.dim }}>{new Date(g.created_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div style={{ color: C.dim, flexShrink: 0 }}>
                  {isEx ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                </div>
              </div>

              {/* Expanded content */}
              {isEx && (
                <div style={{ padding: '0 14px 12px' }}>
                  {isEd ? (
                    <div>
                      <textarea
                        data-testid={`ai-history-edit-${g.gen_id}`}
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        rows={5}
                        style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, color: C.text, fontSize: 11, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button
                          data-testid={`save-edit-${g.gen_id}`}
                          onClick={() => void updateStatus(g.gen_id, 'edited', editContent)}
                          disabled={!!al}
                          style={{ background: `${C.green}15`, border: `1px solid ${C.green}30`, color: C.green, borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          {al ? <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }}/> : <Check size={10}/>} Save
                        </button>
                        <button onClick={() => setEditingId(null)} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 10 }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 11, color: C.muted, lineHeight: 1.55, maxHeight: 150, overflowY: 'auto', marginBottom: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {g.result ?? 'No result'}
                    </div>
                  )}

                  {!isEd && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {g.status !== 'accepted' && (
                        <button
                          data-testid={`accept-btn-${g.gen_id}`}
                          onClick={() => void updateStatus(g.gen_id, 'accepted')}
                          disabled={!!al}
                          style={{ background: `${C.green}12`, border: `1px solid ${C.green}25`, color: C.green, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <Check size={10}/> Accept
                        </button>
                      )}
                      {g.status !== 'rejected' && (
                        <button
                          data-testid={`reject-btn-${g.gen_id}`}
                          onClick={() => void updateStatus(g.gen_id, 'rejected')}
                          disabled={!!al}
                          style={{ background: `${C.red}12`, border: `1px solid ${C.red}25`, color: C.red, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <X size={10}/> Reject
                        </button>
                      )}
                      <button
                        data-testid={`edit-btn-${g.gen_id}`}
                        onClick={() => { setEditingId(g.gen_id); setEditContent(g.result ?? ''); }}
                        style={{ background: `${C.purple}12`, border: `1px solid ${C.purple}25`, color: C.purple, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <Edit3 size={10}/> Edit
                      </button>
                      <button
                        data-testid={`regen-btn-${g.gen_id}`}
                        onClick={() => void doRegenerate(g)}
                        disabled={actionLoading === g.gen_id + '-regen'}
                        style={{ background: `${C.teal}12`, border: `1px solid ${C.teal}25`, color: C.teal, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        {actionLoading === g.gen_id + '-regen' ? <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }}/> : <RotateCcw size={10}/>} Re-run
                      </button>
                      <button
                        data-testid={`copy-btn-${g.gen_id}`}
                        onClick={() => navigator.clipboard.writeText(g.result ?? '')}
                        style={{ background: `${C.blue}12`, border: `1px solid ${C.blue}25`, color: C.blue, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <Copy size={10}/> Copy
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
